import { useState, useEffect } from "react";
import axios from "axios";
import { 
  History, 
  Phone,
  CheckCircle2,
  Clock,
  XCircle,
  PhoneOff,
  PhoneMissed,
  Euro
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COST_PER_MINUTE = 0.10;

export default function HistoryPage() {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchCalls(); }, []);

  const fetchCalls = async () => {
    try {
      const res = await axios.get(`${API}/calls`);
      setCalls(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Failed to fetch calls:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case "completed": return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case "failed": return <XCircle className="w-4 h-4 text-red-500" />;
      case "no-answer": return <PhoneMissed className="w-4 h-4 text-amber-500" />;
      case "busy": return <PhoneOff className="w-4 h-4 text-orange-500" />;
      case "initiated": case "ringing": return <Clock className="w-4 h-4 text-blue-500" />;
      default: return <Phone className="w-4 h-4 text-blue-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      completed: { label: "Abgeschlossen", cls: "bg-green-50 text-green-700" },
      failed: { label: "Fehlgeschlagen", cls: "bg-red-50 text-red-700" },
      "no-answer": { label: "Keine Antwort", cls: "bg-amber-50 text-amber-700" },
      busy: { label: "Besetzt", cls: "bg-orange-50 text-orange-700" },
      initiated: { label: "Gestartet", cls: "bg-blue-50 text-blue-700" },
      ringing: { label: "Klingelt", cls: "bg-blue-50 text-blue-700" },
      answered: { label: "Angenommen", cls: "bg-emerald-50 text-emerald-700" },
    };
    const s = map[status] || { label: status, cls: "bg-slate-100 text-slate-600" };
    return <span className={`px-2 py-0.5 rounded text-xs font-medium ${s.cls}`}>{s.label}</span>;
  };

  const getResponseBadge = (response) => {
    if (!response) return <span className="text-xs text-slate-400">–</span>;
    const map = {
      accepted: { label: "Erfolg", cls: "bg-green-50 text-green-700" },
      callback: { label: "Rückruf", cls: "bg-amber-50 text-amber-700" },
      rejected: { label: "Abgelehnt", cls: "bg-red-50 text-red-700" },
    };
    const s = map[response] || { label: response, cls: "bg-slate-100 text-slate-600" };
    return <span className={`px-2 py-0.5 rounded text-xs font-medium ${s.cls}`}>{s.label}</span>;
  };

  const formatDate = (dateStr) => {
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    }).format(new Date(dateStr));
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "–";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const calcCost = (seconds) => {
    if (!seconds || seconds <= 0) return 0;
    return Math.ceil(seconds / 60) * COST_PER_MINUTE;
  };

  const totalDuration = calls.reduce((sum, c) => sum + (c.duration || 0), 0);
  const totalCost = calls.reduce((sum, c) => sum + calcCost(c.duration), 0);
  const totalMinutes = Math.floor(totalDuration / 60);
  const totalSecs = totalDuration % 60;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 tracking-tight" data-testid="history-title">
          Anruf-Historie
        </h1>
        <p className="text-slate-500 mt-1">{calls.length} Anrufe insgesamt</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-white border border-slate-200/60 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Anrufe</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">{calls.length}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Phone className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200/60 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Gesamtdauer</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">{totalMinutes}:{totalSecs.toString().padStart(2, '0')}</p>
                <p className="text-[10px] text-slate-400">Minuten:Sekunden</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200/60 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Gesamtkosten</p>
                <p className="text-2xl font-bold text-slate-800 mt-1">{totalCost.toFixed(2)} €</p>
                <p className="text-[10px] text-slate-400">0,10 € pro angef. Minute</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Euro className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Calls Table */}
      <Card className="bg-white rounded-xl border border-slate-200/60 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table data-testid="history-table">
            <TableHeader>
              <TableRow className="bg-slate-50/50 border-b border-slate-200">
                <TableHead className="font-semibold text-slate-700 w-12"></TableHead>
                <TableHead className="font-semibold text-slate-700">Kontakt</TableHead>
                <TableHead className="font-semibold text-slate-700">Telefon</TableHead>
                <TableHead className="font-semibold text-slate-700">Kampagne</TableHead>
                <TableHead className="font-semibold text-slate-700">Status</TableHead>
                <TableHead className="font-semibold text-slate-700">Antwort</TableHead>
                <TableHead className="font-semibold text-slate-700">Dauer</TableHead>
                <TableHead className="font-semibold text-slate-700">Kosten</TableHead>
                <TableHead className="font-semibold text-slate-700">Datum</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={9}>
                      <div className="h-12 bg-slate-100 animate-pulse rounded"></div>
                    </TableCell>
                  </TableRow>
                ))
              ) : calls.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-12">
                    <div className="empty-state">
                      <History className="w-12 h-12 text-slate-300 mb-3" />
                      <p className="text-slate-500">Noch keine Anrufe durchgeführt</p>
                      <p className="text-slate-400 text-sm mt-1">Starten Sie Ihre erste Anrufkampagne</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {calls.map((call) => (
                    <TableRow 
                      key={call.id} 
                      className="table-row-hover border-b border-slate-100"
                      data-testid={`history-row-${call.id}`}
                    >
                      <TableCell>
                        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                          {getStatusIcon(call.status)}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium text-slate-900">{call.contact_name}</TableCell>
                      <TableCell className="text-slate-600">{call.contact_phone}</TableCell>
                      <TableCell className="text-slate-600">{call.campaign_name}</TableCell>
                      <TableCell>{getStatusBadge(call.status)}</TableCell>
                      <TableCell>{getResponseBadge(call.response)}</TableCell>
                      <TableCell className="text-slate-600 font-mono text-sm">{formatDuration(call.duration)}</TableCell>
                      <TableCell className="font-semibold text-slate-800 text-sm">
                        {call.duration > 0 ? `${calcCost(call.duration).toFixed(2)} €` : "–"}
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">{formatDate(call.created_at)}</TableCell>
                    </TableRow>
                  ))}
                  {/* Summenzeile */}
                  <TableRow className="bg-slate-50 border-t-2 border-slate-200">
                    <TableCell colSpan={6} className="font-bold text-slate-700 text-right">Gesamt:</TableCell>
                    <TableCell className="font-bold text-slate-800 font-mono">{totalMinutes}:{totalSecs.toString().padStart(2, '0')}</TableCell>
                    <TableCell className="font-bold text-slate-800">{totalCost.toFixed(2)} €</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
